/**
 * Loader for the Student Support chat widget.
 *
 * Only activates on local development hosts (`localhost`, `127.0.0.1`) and
 * on hosts whose name starts with "test" (e.g. test.nsuk.edu.ng). It is a
 * silent no-op on production.
 *
 * Why the host gate lives here: the widget is third-party and meant for
 * pre-production environments only. Centralising the rule means both the
 * login page (guest mode) and the student shell (authenticated mode)
 * stay in sync.
 */

const SCRIPT_ID = 'student-support-widget-script';
const STYLE_ID = 'student-support-widget-style';
const SCRIPT_URL = 'https://student-support-widget.fly.dev/student-support-widget.iife.js';
const STYLE_URL = 'https://student-support-widget.fly.dev/style.css';
const BACKEND_URL = 'https://student-support-backend.fly.dev';

let scriptPromise = null;
let initialized = false;

function isEligibleHost() {
  const host = (window.location.hostname || '').toLowerCase();
  return host === 'localhost' || host === '127.0.0.1' || host.startsWith('test');
}

function loadAssets() {
  if (scriptPromise) return scriptPromise;

  if (!document.getElementById(STYLE_ID)) {
    const link = document.createElement('link');
    link.id = STYLE_ID;
    link.rel = 'stylesheet';
    link.href = STYLE_URL;
    document.head.appendChild(link);
  }

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      if (window.StudentSupportWidget) return resolve(window.StudentSupportWidget);
      existing.addEventListener('load', () => resolve(window.StudentSupportWidget));
      existing.addEventListener('error', reject);
      return;
    }
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve(window.StudentSupportWidget);
    script.onerror = reject;
    document.body.appendChild(script);
  });

  return scriptPromise;
}

const StudentSupportWidget = {
  /**
   * Mount the widget if the current host is local or starts with "test".
   * No-op on prod or if already initialised. To re-init with different
   * config (e.g. after login flips the mode), call `destroy()` first.
   *
   * @param {object} config Forwarded to StudentSupportWidget.init().
   *   - studentId / studentName / studentEmail switch the widget into
   *     authenticated mode (see widget docs).
   */
  async load(config = {}) {
    if (!isEligibleHost() || initialized) return;

    try {
      const widget = await loadAssets();
      if (!widget || typeof widget.init !== 'function') return;
      widget.init({ backendUrl: BACKEND_URL, ...config });
      initialized = true;
    } catch (e) {
      // Loading is best-effort — don't block the host app on failure.
      // eslint-disable-next-line no-console
      console.warn('[StudentSupportWidget] failed to load', e);
    }
  },

  destroy() {
    if (window.StudentSupportWidget && typeof window.StudentSupportWidget.destroy === 'function') {
      window.StudentSupportWidget.destroy();
    }
    initialized = false;
  },

  isEligibleHost,
};

export default StudentSupportWidget;
